import { WikiArticleReviewDownloadDto, WikiSectionDownloadDto } from 'collaboration-service';
import { AutoGrid, Button, Icon, useBelowBreakpoint } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'services/ApplicationState/ApplicationState';
import { searchHtmlAndMark, searchTableAndMark, shallowCompare } from 'services/Helpers';
import { useCheckWhereIAm, usePrefixedRouteHelpers } from 'services/Helpers/RoutingHelper';
import ScrollHelper from 'services/Helpers/ScrollHelper';
import { getTranslated } from 'services/Helpers/TranslationHelpers';
import { useImgI18N } from 'services/ImgI18N';
import { WikiSC as T } from './WikiSC';
import { wikiTheme } from './WikiTheme';
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard';
import WikiSectionColumn from './WikiSectionColumn';

const mapper = (state: ApplicationState) => ({
    search: state.wikiState.search,
    contentLanguage: state.contentLanguage,
    routeParams: state.params,
    config: state.wikiState.config,
});

const EMPTY_STRING_ARRAY: string[] = [];

export interface WikiSectionProps {
    section: WikiSectionDownloadDto;
    scrollHelper?: ScrollHelper;
    onIds?: (ids?: string[]) => void;
    addRefs: (name: string, ids?: string[], threshold?: number | number[]) => (r: HTMLDivElement | null) => void;
    reviewMode?: boolean;
    setEditMode?: (editMode: boolean) => void;
    isInEditMode?: boolean;
    numberingEnabled: boolean;
    numberingLevels?: (string[] | string)[];
    index: number;
    reviews?: WikiArticleReviewDownloadDto[];
    onNewReview?: (review: WikiArticleReviewDownloadDto) => Promise<void>;
    availableLngs?: string[];
}

export interface WikiSectionState {
    open: boolean;
    width: number;
}

const getStartTag = (id: string) => {
    return `<b id="#searchResult${id}-{{pos}}" class="searchFinding">` +
        `<b  style="position: absolute; top: -60px; left: 0px" id="#searchResultAnchor${id}-{{pos}}"></b>`;
};

const fromClipboardLink = <T.PulsateIcon>
    <Icon name="chevron right double" style={{ float: "left", marginRight: 5, marginLeft: -3 }} marginTop={5} />
</T.PulsateIcon>;


const WikiSection = (p: WikiSectionProps) => {
    const [open, setOpen] = React.useState<boolean>(true);
    const [activeColumnIds, setActiveColumnIds] = React.useState<string[]>([]);
    const { section: s, scrollHelper, onIds, addRefs, reviewMode,
        setEditMode, isInEditMode, numberingEnabled, numberingLevels, index,
        reviews, onNewReview, availableLngs } = p;
    const { contentLanguage, routeParams, search, config } = useSelector(mapper, shallowCompare);
    const { t, currentNamespace } = useImgI18N(config.namespace ?? "wiki");

    const { createNamedRoute } = usePrefixedRouteHelpers("wiki", config.baseRoute);

    const secTitle = getTranslated(s.title, contentLanguage).text;
    const minimize = wikiTheme.articleTheme.minimizeIcon ?? "minimize";
    const maximize = wikiTheme.articleTheme.maximizeIcon ?? "maximize";
    const articleId = routeParams?.id as string;
    const routeSecId = routeParams?.secId as string;
    const routeColId = routeParams?.colId as string;

    React.useEffect(() => {
        if (reviewMode === true)
            setOpen(true);
    }, [reviewMode]);

    React.useEffect(() => {
        if (search === undefined && onIds)
            onIds(undefined);
    }, [search, onIds]);

    const onFindings = React.useCallback((id: string, ids: string[]) => {
        if (ids.length !== 0) {
            if (onIds)
                onIds(ids);
        }
    }, [onIds]);

    React.useEffect(() => {
        if (activeColumnIds.length > 0 && setEditMode)
            setEditMode(true);
        else if (setEditMode)
            setEditMode(false);
    }, [activeColumnIds, setEditMode]);

    const isSmallDevice = useBelowBreakpoint("desktop");
    const sectionId = s.id;
    const urlSec = articleId ? window.location.origin + "/#" + createNamedRoute("wiki_article", { id: articleId, secId: sectionId }) : undefined;
    const isLinkedSec = routeSecId === sectionId && (routeColId === undefined || routeColId === "");

    const check = useCheckWhereIAm();
    const isFaq = check("qanda") || check("q&a_search");

    React.useEffect(() => {
        _.forEach(s.columns, c => {
            let table = getTranslated(c.tableContent, contentLanguage);
            let text = getTranslated(c.textContent, contentLanguage).text;
            if (search) {
                const starttag = getStartTag(c.id);
                const endtag = "</b>";

                let findings = 0;
                const { count } = searchHtmlAndMark(text, search, starttag, endtag);
                findings += count;

                if (c.columnType === "Table") {
                    const { currentCount } = searchTableAndMark(table, search, starttag, endtag);
                    findings += currentCount;
                }
                onFindings(c.id, _.times(findings, x => `${c.id}-${x}`));
            }
            else
                onFindings(c.id, []);
        });
    }, [s, contentLanguage, search, onFindings]);

    const label = numberingEnabled ? numberingLevels?.[0]?.[index] : undefined;

    const sectionDivRef = React.useCallback((el: HTMLDivElement | null) => {
        addRefs("section", [sectionId], 0.1)(el);
    }, [addRefs, sectionId]);

    const toggleOpen = React.useCallback(() => setOpen(o => !o), []);

    return (
        <div ref={sectionDivRef}>
            {isFaq ?
                <T.SectionTitle
                    isLinkedSec={false}
                    open={true}
                    noTitle={!secTitle || secTitle === ""}
                    style={{ marginBottom: 10, fontSize: "1.1em" }}
                >
                    {secTitle}
                </T.SectionTitle>
                :
                <T.SectionTitle
                    isLinkedSec={isLinkedSec ?? false}
                    open={open}
                    lang={contentLanguage}
                    noTitle={!secTitle || secTitle === ""}
                >
                    {!reviewMode &&
                        <Button
                            kind="transparentButton"
                            icon={open ? minimize : maximize}
                            onClick={toggleOpen}
                            floated="right"
                        />
                    }
                    {!open &&
                        <Icon name="eye close" color="rgba(0,0,0,0.3)" size="20px" style={{ float: "left", marginRight: 10, marginTop: 5, marginLeft: 10 }} />
                    }
                    {!secTitle || secTitle === "" ? <span>&nbsp;</span> :
                        <>
                            {isLinkedSec && fromClipboardLink}
                            {label &&
                                <T.ColumnTitleLabel large={true} revMode={reviewMode}>
                                    {label}
                                </T.ColumnTitleLabel>
                            }
                            {secTitle}
                            {urlSec && open && !reviewMode &&
                                <CopyToClipboard
                                    type='icon'
                                    text={urlSec}
                                    copyHint={t("copy link to clipboard")}
                                    copiedHint={t("link copied to clipboard")}
                                />
                            }
                        </>
                    }
                </T.SectionTitle>
            }

            {open &&
                <>
                    {(s.mode === "Grid" || s.mode === "Swipe") &&
                        <AutoGrid
                            columns={isSmallDevice ? 1 : s.columnCount}
                            gap={10}
                        >
                            {_.map(s.columns, (c, i) => {
                                const colSpan = reviewMode && activeColumnIds.includes(c.id) ? s.columnCount : c.columnWidth;
                                return (
                                    <WikiSectionColumn
                                        index={i}
                                        span={colSpan}
                                        key={c.id}
                                        column={c}
                                        reviewMode={reviewMode}
                                        activeColumnIds={activeColumnIds}
                                        setActiveColumnIds={setActiveColumnIds}
                                        onNewReview={onNewReview}
                                        reviews={reviews}
                                        namespace={currentNamespace}
                                        numberingEnabled={numberingEnabled}
                                        numberingLevels={numberingLevels}
                                        parentLabel={label}
                                        section={s}
                                        isInEditMode={isInEditMode}
                                        addRefs={addRefs}
                                        scrollHelper={scrollHelper}
                                        availableLngs={availableLngs ?? EMPTY_STRING_ARRAY}
                                    />
                                );
                            })}
                        </AutoGrid>
                    }
                </>
            }
        </div>
    );
}

export type { WikiSectionColumnProps } from './WikiSectionColumn';
export { default as WikiSectionColumn } from './WikiSectionColumn';


export default WikiSection;

